import { MathUtils, Matrix3, Mesh, Vector3 } from 'three';

export interface UVBounds {
  uMin: number;
  uMax: number;
  vMin: number;
  vMax: number;
}

export interface FacePanel {
  normal: Vector3;
  centroid: Vector3;
  meshes: { mesh: Mesh; originalMaterial: Mesh['material'] }[];
  uvBounds: UVBounds;
}

// The shared outside material's texture IS the Fabric canvas (see phase 3a), so each Face's UV
// coordinates map directly onto canvas pixel space - clicking a point on the 2D canvas can be
// resolved back to "which physical panel is this" by finding whose UV bounding box contains it.
export function computeFaceUVBounds(outsideMeshes: Mesh[]): UVBounds {
  let uMin = Infinity;
  let uMax = -Infinity;
  let vMin = Infinity;
  let vMax = -Infinity;
  outsideMeshes.forEach((mesh) => {
    const uvAttr = mesh.geometry.attributes['uv'];
    if (!uvAttr) {
      return;
    }
    for (let i = 0; i < uvAttr.count; i++) {
      const u = uvAttr.getX(i);
      const v = uvAttr.getY(i);
      uMin = Math.min(uMin, u);
      uMax = Math.max(uMax, u);
      vMin = Math.min(vMin, v);
      vMax = Math.max(vMax, v);
    }
  });
  return { uMin, uMax, vMin, vMax };
}

export function computeFaceGeometry(mesh: Mesh): { normal: Vector3; centroid: Vector3 } {
  const geometry = mesh.geometry;
  const positionAttr = geometry.attributes['position'];
  const normalAttr = geometry.attributes['normal'];

  const centroid = new Vector3();
  for (let i = 0; i < positionAttr.count; i++) {
    centroid.add(new Vector3().fromBufferAttribute(positionAttr, i));
  }
  centroid.divideScalar(positionAttr.count);
  mesh.localToWorld(centroid);

  const normal = normalAttr ? new Vector3().fromBufferAttribute(normalAttr, 0) : new Vector3(0, 1, 0);
  const normalMatrix = new Matrix3().getNormalMatrix(mesh.matrixWorld);
  normal.applyMatrix3(normalMatrix).normalize();

  return { normal, centroid };
}

// Fit on whichever axis is tighter (vertical FOV vs. the aspect-derived horizontal FOV), so a
// narrow/tall viewport doesn't crop the sides, and a wide one doesn't crop top/bottom. Split out
// of design-3d.component.ts's fitCameraToAvatar() so compensateZoomForSplit() can re-derive a
// distance for a hypothetical (not-yet-laid-out) aspect ratio too, off the same
// avatarBoundingRadius, without re-measuring the model's geometry.
export function fittedDistanceForAspect(fovDegrees: number, aspect: number, avatarBoundingRadius: number): number {
  const verticalFov = MathUtils.degToRad(fovDegrees);
  const horizontalFov = 2 * Math.atan(Math.tan(verticalFov / 2) * aspect);
  const tightestFov = Math.min(verticalFov, horizontalFov);
  const FIT_MARGIN = 1.2;
  return (avatarBoundingRadius / Math.sin(tightestFov / 2)) * FIT_MARGIN;
}

// Fixed elevation the default view is held at, matching the reference 3/4 angle in
// upload-preview.component.ts (position (4.5, 2, 0) from a target at the origin:
// atan2(2, 4.5) ~= 24 deg) - see [[design3d_default_camera_angle]].
const DEFAULT_PANEL_ELEVATION_RADIANS = Math.atan2(2, 4.5);
// Rotates the horizontal bearing around world up before the elevation is applied, so the
// default view sits a few degrees off dead-on instead of centered on the panel - reveals a
// sliver of the box's right side alongside the front, instead of a flat head-on shot. Positive
// angle here (three.js's Vector3.applyAxisAngle right-hand rule around +Y) is what produces
// "right" - confirmed visually (a new side panel became visible on the right that wasn't before)
// rather than derived, since the sign depends on which way each box style's panel normals face.
const DEFAULT_PANEL_AZIMUTH_TILT_RADIANS = (15 * Math.PI) / 180;

// Positions the camera to instantly face whichever panel has the largest printable area - see
// design-3d.component.ts's faceDefaultPanel()/resetViewToInitialAngle() for the full rationale.
// The panel's raw normal alone puts the camera dead level with that panel; this discards the
// normal's own vertical component and keeps only its horizontal bearing (which side of the box
// we're viewing from), then rebuilds the direction from that bearing at a fixed elevation angle -
// see the original comment history in design-3d.component.ts for why.
export function computeDefaultPanelDirection(facePanels: FacePanel[]): Vector3 | undefined {
  if (!facePanels.length) {
    return undefined;
  }
  const panelArea = (panel: FacePanel): number =>
    (panel.uvBounds.uMax - panel.uvBounds.uMin) * (panel.uvBounds.vMax - panel.uvBounds.vMin);
  const defaultPanel = facePanels.reduce((largest, panel) => (panelArea(panel) > panelArea(largest) ? panel : largest));

  const horizontal = new Vector3(defaultPanel.normal.x, 0, defaultPanel.normal.z);
  // Top/bottom panels have a near-zero horizontal component - fall back to the fedex reference
  // bearing (+X) rather than normalizing a near-zero-length vector into noise.
  const bearing = horizontal.lengthSq() > 1e-6 ? horizontal.normalize() : new Vector3(1, 0, 0);
  bearing.applyAxisAngle(new Vector3(0, 1, 0), DEFAULT_PANEL_AZIMUTH_TILT_RADIANS);
  return bearing
    .multiplyScalar(Math.cos(DEFAULT_PANEL_ELEVATION_RADIANS))
    .add(new Vector3(0, Math.sin(DEFAULT_PANEL_ELEVATION_RADIANS), 0));
}

// Any click on the 2D canvas - empty space or a design object - is resolved to a panel via UV
// bounding boxes. scenePoint is always canvas-absolute (real Fabric mouse events already are;
// callers building a synthetic point pass canvas-absolute fabricImage.left/top too) - the margin
// is subtracted here to land back in the design area's own 0..canvasWidthPx/canvasHeightPx space
// that UV bounds are expressed in.
export function findPanelAt(
  scenePoint: { x: number; y: number },
  canvasWidthPx: number,
  canvasHeightPx: number,
  canvasMargin: number,
  facePanels: FacePanel[],
): FacePanel | undefined {
  if (!canvasWidthPx || !canvasHeightPx) {
    return undefined;
  }
  const u = (scenePoint.x - canvasMargin) / canvasWidthPx;
  // CanvasTexture defaults to flipY=true, so UV v=0 is the bottom of the source canvas, not the
  // top - matches the same top/bottom flip already relied on for the design texture itself.
  const v = 1 - (scenePoint.y - canvasMargin) / canvasHeightPx;
  return facePanels.find((p) => u >= p.uvBounds.uMin && u <= p.uvBounds.uMax && v >= p.uvBounds.vMin && v <= p.uvBounds.vMax);
}

// Converts a panel's UV bounds into canvas-pixel bounds (design-area space, canvasMargin-offset)
// - shared by design-3d.component.ts's getFaceBoundsForObject() (align-on-panel) and
// fillPanelAt() (fill tool).
export function getPanelPixelBounds(
  panel: FacePanel,
  canvasWidthPx: number,
  canvasHeightPx: number,
  canvasMargin: number,
): { left: number; right: number; top: number; bottom: number } {
  return {
    left: panel.uvBounds.uMin * canvasWidthPx + canvasMargin,
    right: panel.uvBounds.uMax * canvasWidthPx + canvasMargin,
    top: (1 - panel.uvBounds.vMax) * canvasHeightPx + canvasMargin,
    bottom: (1 - panel.uvBounds.vMin) * canvasHeightPx + canvasMargin,
  };
}

// Maps a live camera-to-target distance onto the slider's 0-100 range: 0 at maxDistance (zoomed
// all the way out), 100 at minDistance (zoomed all the way in). fallback is returned unchanged
// when minDistance/maxDistance aren't a valid range yet (mirrors design-3d.component.ts's
// percentForDistance(), which falls back to the component's own current cameraZoomPercent).
export function percentForDistance(distance: number, minDistance: number, maxDistance: number, fallback: number): number {
  if (!Number.isFinite(minDistance) || !Number.isFinite(maxDistance) || maxDistance <= minDistance) {
    return fallback;
  }
  const clamped = MathUtils.clamp(distance, minDistance, maxDistance);
  return ((maxDistance - clamped) / (maxDistance - minDistance)) * 100;
}
